'use client';

import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import type { Document } from '@contentful/rich-text-types';
import { useRouter } from '@/lib/i18n/navigation';
import { useLocale, useTranslations } from 'next-intl';

import {
  orderBoutiqueCountryOptions,
  type BoutiqueCountryOption,
} from '@/lib/boutique/countryLabels';
import { navigateToBoutiquesListing } from '@/lib/boutique/navigateToBoutiquesListing';
import { getLanguageFromLocale } from '@/lib/i18n/config';
import type { CMSPlaceholder } from '@/lib/cms/types';

import styles from './BoutiquesListingInfo.module.scss';

export interface BoutiquesListingInfoProps {
  placeholder: CMSPlaceholder;
  countryCode: string;
  countryOptions: BoutiqueCountryOption[];
}

/**
 * Boutiques listing intro from CMS Placeholder (country selector + optional copy).
 */
export function BoutiquesListingInfo({
  placeholder,
  countryCode,
  countryOptions,
}: Readonly<BoutiquesListingInfoProps>) {
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations('boutiquesPage');
  const languageCode = getLanguageFromLocale(locale);
  const orderedOptions = orderBoutiqueCountryOptions(countryOptions, languageCode, countryCode);

  const description = placeholder.subtext?.json
    ? documentToPlainTextString(placeholder.subtext.json as Document).trim()
    : null;

  const handleCountryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextCountry = event.target.value;
    if (!nextCountry) {
      return;
    }

    navigateToBoutiquesListing(nextCountry, router);
  };

  return (
    <section className={styles.section} aria-labelledby="boutiques-listing-info-heading">
      {placeholder.heading ? (
        <h2 id="boutiques-listing-info-heading" className={styles.heading}>
          {placeholder.heading}
        </h2>
      ) : (
        <h2 id="boutiques-listing-info-heading" className={styles.visuallyHidden}>
          {t('pageTitle', { country: countryCode })}
        </h2>
      )}

      {description ? <p className={styles.description}>{description}</p> : null}

      {orderedOptions.length > 0 ? (
        <div className={styles.countryField}>
          <label htmlFor="boutiques-country-select" className={styles.countryLabel}>
            {t('selectCountry')}
          </label>
          <select
            id="boutiques-country-select"
            name="country"
            className={styles.countrySelect}
            value={countryCode}
            onChange={handleCountryChange}
          >
            {orderedOptions.map((option) => (
              <option key={option.code} value={option.code}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      ) : null}
    </section>
  );
}

BoutiquesListingInfo.displayName = 'BoutiquesListingInfo';
